/* global AbortSignal */
/** Клиент «Аналитики МП» по площадке: Ozon — `/mp-analytics/ozon/*` (ozonAnalytics.ts), WB —
 *  `/mp-analytics/wb/*` (backend `mp_finance/`). Общие ручки (meta, summary, trend, tree, product)
 *  у площадок 1:1 — `analyticsApi(mp)`; сверка у WB своя (по отчётам WB) — `getWbRecon`.
 *  Только у Ozon: поставки, карточки поставки и кампании — именованные функции ozonAnalytics.ts. */
import { api } from "./client";
import * as oz from "./ozonAnalytics";
import type { AnalyticsApi, CogsView, MartState } from "./ozonAnalytics";

export type Mp = "ozon" | "wb";

export const MP_IDS: readonly Mp[] = ["ozon", "wb"];

const WB_BASE = "/mp-analytics/wb";

let wbApi: AnalyticsApi | null = null;

/** Общие ручки площадки. Ozon — именованные экспорты ozonAnalytics.ts (база `/mp-analytics/ozon`,
 *  тот же модуль, что и у ручек «только Ozon»), WB — тот же клиент с базой `/mp-analytics/wb`;
 *  ключ артикула `/product` у обеих — `vendor_code`. */
export function analyticsApi(mp: Mp): AnalyticsApi {
  if (mp === "ozon") return oz;
  wbApi ??= oz.createAnalyticsApi({ base: WB_BASE, productParam: "vendor_code" });
  return wbApi;
}

/** Статус сверки месяца / отчёта WB: ok — сошлось с шапкой отчёта, mismatch — нет, no_header —
 *  шапки WB нет (не с чем сверить), pending — шапка недели есть, строк отчёта ещё нет (ждёт
 *  недельного отчёта; у месяца — есть такой отчёт, а расхождений и дыр шапки нет). */
export type WbReconStatus = "ok" | "mismatch" | "no_header" | "pending";

/** Строка сверки: столбец ЛК WB (`label`, порядок — `sort`) за месяц × юрлицо. Знак — как в ЛК
 *  (удержания положительными). null — суммы нет (у statement — нет шапки WB). */
export interface WbReconRow {
  month: string;
  legal_name: string;
  label: string;
  sort: number;
  accrual: number | null;
  statement: number | null;
  diff: number | null;
  note: string | null;
  has_header: boolean;
}

/** Итог месяца × юрлица — по строке «Итого к оплате». */
export interface WbReconMonth {
  month: string;
  legal_name: string;
  accrual: number | null;
  statement: number | null;
  diff: number | null;
  status: WbReconStatus;
  unexplained_abs: number;
}

export interface WbReportRow {
  label: string;
  sort: number;
  accrual: number | null;
  statement: number | null;
  diff: number | null;
  note: string | null;
}

/** Отчёт реализации WB: 1 — «Основной», 2 — «По выкупам» (null — тип не указан); месяц — по
 *  началу отчёта. */
export interface WbReport {
  report_id: number | string;
  report_type: number | null;
  period: "daily" | "weekly";
  legal_name: string;
  month: string;
  date_from: string;
  date_to: string;
  realization_rows: number;
  has_header: boolean;
  status: WbReconStatus;
  rows: WbReportRow[];
}

export interface WbRecon {
  rows: WbReconRow[];
  months: WbReconMonth[];
  reports: WbReport[];
  mart: MartState;
  from_cache: boolean;
}

/** Сверка WB по отчётам за месяцы `YYYY-MM` (месяц — по началу отчёта). */
export async function getWbRecon(
  monthFrom: string,
  monthTo: string,
  legal: string | null,
  signal?: AbortSignal,
): Promise<WbRecon> {
  const params: Record<string, string> = { month_from: monthFrom, month_to: monthTo };
  if (legal) params.legal = legal;
  const { data } = await api.get<WbRecon>(`${WB_BASE}/recon`, { params, signal });
  return data;
}

/** Статус покрытия продажи себестоимостью (`mart.mp_cogs_coverage`): ok — цена есть;
 *  no_price — продукт 1С есть, цены на дату нет; unmapped — оффера нет в 1С; ambiguous — оффер у
 *  нескольких продуктов 1С; no_offer — продажа технической карточки без оффера; before_start —
 *  возврат продажи до начала себестоимости (в плашку «нет цены» не входит). */
export type CogsStatus =
  | "ok"
  | "no_price"
  | "unmapped"
  | "ambiguous"
  | "no_offer"
  /** возврат продажи, сделанной до начала себестоимости: цены и не должно быть — не проблема */
  | "before_start";

export interface CogsStatusTotals {
  /** разных офферов */
  offers: number;
  /** разных товаров: у ok/no_price — характеристик 1С (как в «Без цены»), у прочих — офферов */
  items: number;
  qty: number;
  /** выручка по цене продавца, ₽ с НДС */
  revenue: number;
}

export interface CogsCoverage {
  date_from: string;
  date_to: string;
  legal: string | null;
  /** с какой даты период покрыт себестоимостью; null — цен нет или период целиком раньше */
  covered_from: string | null;
  statuses: Record<CogsStatus, CogsStatusTotals>;
  cogs: CogsView & { state: Record<string, unknown> };
  mart: MartState;
  from_cache: boolean;
}

/** Покрытие продаж периода себестоимостью (обе площадки; `/mp-analytics/{mp}/cogs-coverage`). */
export async function getCogsCoverage(
  mp: Mp,
  dateFrom: string,
  dateTo: string,
  legal: string | null,
  signal?: AbortSignal,
): Promise<CogsCoverage> {
  const params: Record<string, string> = { date_from: dateFrom, date_to: dateTo };
  if (legal) params.legal = legal;
  const { data } = await api.get<CogsCoverage>(`/mp-analytics/${mp}/cogs-coverage`, { params, signal });
  return data;
}

/** Переключатель заказов матрицы «По дням»: всего (включая «канал не определён») / FBO / FBS. */
export type DailyChannel = "all" | "fbo" | "fbs";

/** Итог недели строки матрицы: Σ дней недели с данными (`n_days`), Δ дн — последний день недели С
 *  ДАННЫМИ (`d_day_at`; у текущей недели — последний полный день) к предыдущему, Δ нед — к тем же
 *  дням прошлой недели. Δ — в знаке витрины: у сумм — относительное изменение, у долей
 *  (`unit = pct`) — разница долей; null — не посчитать (пусто). */
export interface DailyWeekCell {
  total: number | null;
  d_day: number | null;
  /** день, к которому относится Δ дн; null — в неделе нет дней с данными */
  d_day_at: string | null;
  d_week: number | null;
  n_days: number;
}

/** Строка матрицы. Значения — в знаке витрины (уменьшающее — минус: удержания, отмены,
 *  себестоимость, ДРР); `kind = expense` показывается с обратным знаком (как сводка). null — дня без
 *  данных / не определено (себестоимость до `cogs_from`): пустая ячейка, не 0. */
export interface DailyRow {
  key: string;
  label: string;
  title: string | null;
  unit: "qty" | "rub" | "pct";
  kind: "income" | "expense";
  section: "orders" | "pnl" | "result" | "reference";
  /** 1 — статья группы (`parent` — ключ строки группы) */
  level: 0 | 1;
  /** группа P&L строки (окно графика); null — не строка P&L */
  grp: string | null;
  grp_label: string | null;
  item: string | null;
  parent: string | null;
  reference?: boolean;
  /** строка от себестоимости (себестоимость, расходы, маржа, маржа %): null раньше `cogs_from` —
   *  «—» с подсказкой, а не пусто */
  cogs?: boolean;
  /** по дням оси `MpDaily.days` */
  days: (number | null)[];
  /** по неделям `MpDaily.weeks` */
  weeks: DailyWeekCell[];
  /** итог месяца — только дни месяца */
  month: number | null;
}

/** Источник свежести кабинета: по какой день его данные полны (`through`). `source` — ключ
 *  `data_through` (`date_to` — последний день витрины), `value` — сырое значение. */
export interface DailySpanSource {
  source: string;
  label: string;
  value: string | null;
  through: string | null;
}

/** Дни с данными источника строк: `to` — по свежести загрузчиков кабинетов (все юрлица — минимум по
 *  действующим), `limit` — кто ограничил (`legal` null — последний день витрины); `cabinets` — срок
 *  каждого кабинета (`holds` — держит ли срок «всех юрлиц»). */
export interface DailySpan {
  from: string | null;
  to: string | null;
  limit: (DailySpanSource & { legal: string | null }) | null;
  cabinets: {
    legal: string;
    active: boolean;
    through: string | null;
    holds: boolean;
    limit: DailySpanSource | null;
  }[];
}

/** Строка `mart.mp_orders_state`: `data_through` — по кабинетам (`active`, `last_order_date`, …). */
export interface OrdersMartState {
  built_at: string;
  date_from: string;
  date_to: string;
  data_through: Record<string, Record<string, unknown>>;
}

/** Ответ `GET /mp-analytics/{mp}/daily` — матрица «показатели × дни» месяца. */
export interface MpDaily {
  month: string;
  month_from: string;
  month_to: string;
  /** ось: с понедельника недели 1-го числа по последний день месяца */
  days: string[];
  /** недели Пн–Вс (последняя — по конец месяца); столбцы недели — после дня `to` */
  weeks: { n: number; from: string; to: string }[];
  /** дни с данными: заказы — `mp_orders_state`, финансы — `mp_finance_state` (по кабинетам) */
  spans: { orders: DailySpan; finance: DailySpan };
  rows: DailyRow[];
  /** заказы месяца без канала (WB до 01.06.2026): только во «Всего»; null — таких нет */
  null_channel: { qty: number; amount: number } | null;
  orders: OrdersMartState;
  /** покрытие — с первого дня оси (`covered_cur` false — у недели 1 нет себестоимости) */
  cogs: CogsView;
  legal: string | null;
  channel: DailyChannel;
  mart: MartState;
  from_cache: boolean;
}

/** Матрица «По дням» месяца `YYYY-MM` площадки. */
export async function getDaily(
  mp: Mp,
  month: string,
  legal: string | null,
  channel: DailyChannel,
  signal?: AbortSignal,
): Promise<MpDaily> {
  const params: Record<string, string> = { month, channel };
  if (legal) params.legal = legal;
  const { data } = await api.get<MpDaily>(`/mp-analytics/${mp}/daily`, { params, signal });
  return data;
}
